/**
 * NotificationChannelEditor.tsx
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Editor component for configuring notification channels.
 * Supports Email, Teams, and Webhook channel types with destination validation.
 */

import React, { useCallback } from 'react';
import { Plus, Trash2, Mail, MessageSquare, Webhook } from 'lucide-react';
import type {
  NotificationChannel,
  NotificationChannelType,
} from '@shared/types/alertRuleManagement';

// =============================================================================
// Types
// =============================================================================

export interface NotificationChannelEditorProps {
  channels: NotificationChannel[];
  onChange: (channels: NotificationChannel[]) => void;
  disabled?: boolean;
  error?: string;
}

// =============================================================================
// Channel Type Configuration
// =============================================================================

const CHANNEL_TYPES: {
  value: NotificationChannelType;
  label: string;
  icon: React.FC<{ className?: string }>;
  placeholder: string;
  pattern?: RegExp;
  patternMessage?: string;
}[] = [
  {
    value: 'Email',
    label: 'Email',
    icon: Mail,
    placeholder: 'admin@company.com',
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    patternMessage: 'Invalid email address format',
  },
  {
    value: 'Teams',
    label: 'Microsoft Teams',
    icon: MessageSquare,
    placeholder: 'https://teams.microsoft.com/...',
    pattern: /^https:\/\/.*\.webhook\.office\.com\//i,
    patternMessage: 'Must be a valid Teams webhook URL',
  },
  {
    value: 'Webhook',
    label: 'Webhook',
    icon: Webhook,
    placeholder: 'https://api.example.com/webhook',
    pattern: /^https?:\/\/.+/,
    patternMessage: 'Must be a valid URL (http or https)',
  },
];

// =============================================================================
// Helper Functions
// =============================================================================

function getChannelConfig(type: NotificationChannelType) {
  return CHANNEL_TYPES.find((c) => c.value === type) ?? CHANNEL_TYPES[0];
}

function validateDestination(type: NotificationChannelType, destination: string): string | null {
  const config = getChannelConfig(type);
  if (!destination.trim()) {
    return 'Destination is required';
  }
  if (config.pattern && !config.pattern.test(destination)) {
    return config.patternMessage ?? 'Invalid format';
  }
  return null;
}

// =============================================================================
// Component
// =============================================================================

export const NotificationChannelEditor: React.FC<NotificationChannelEditorProps> = ({
  channels,
  onChange,
  disabled = false,
  error,
}) => {
  // Add new channel
  const handleAddChannel = useCallback(() => {
    onChange([...channels, { type: 'Email', destination: '' }]);
  }, [channels, onChange]);

  // Remove channel
  const handleRemoveChannel = useCallback(
    (index: number) => {
      onChange(channels.filter((_, i) => i !== index));
    },
    [channels, onChange]
  );

  // Update channel type
  const handleTypeChange = useCallback(
    (index: number, type: NotificationChannelType) => {
      const updated = [...channels];
      updated[index] = { ...updated[index], type };
      onChange(updated);
    },
    [channels, onChange]
  );

  // Update channel destination
  const handleDestinationChange = useCallback(
    (index: number, destination: string) => {
      const updated = [...channels];
      updated[index] = { ...updated[index], destination };
      onChange(updated);
    },
    [channels, onChange]
  );

  return (
    <div className="space-y-3">
      {/* Channel List */}
      {channels.map((channel, index) => {
        const config = getChannelConfig(channel.type);
        const Icon = config.icon;
        const validationError = channel.destination
          ? validateDestination(channel.type, channel.destination)
          : null;

        return (
          <div
            key={index}
            className="flex items-start gap-2 p-3 bg-[var(--color-bg-subtle)] rounded-lg border border-[var(--color-border)]"
          >
            {/* Channel Type Selector */}
            <div className="flex-shrink-0 w-40">
              <select
                value={channel.type}
                onChange={(e) => handleTypeChange(index, e.target.value as NotificationChannelType)}
                disabled={disabled}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {CHANNEL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Destination Input */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon className="h-4 w-4 text-[var(--color-text-secondary)]" />
                </div>
                <input
                  type="text"
                  value={channel.destination}
                  onChange={(e) => handleDestinationChange(index, e.target.value)}
                  placeholder={config.placeholder}
                  disabled={disabled}
                  className={`w-full pl-10 pr-3 py-2 text-sm border rounded-md bg-[var(--color-bg-card)] text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] disabled:opacity-50 disabled:cursor-not-allowed ${
                    validationError
                      ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
                      : 'border-[var(--color-border)] focus:border-brand focus:ring-brand'
                  }`}
                />
              </div>
              {validationError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationError}</p>
              )}
            </div>

            {/* Remove Button */}
            <button
              type="button"
              onClick={() => handleRemoveChannel(index)}
              disabled={disabled}
              className="flex-shrink-0 p-2 text-[var(--color-text-secondary)] hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove channel"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      {/* Add Channel Button */}
      <button
        type="button"
        onClick={handleAddChannel}
        disabled={disabled}
        className="inline-flex items-center px-3 py-2 text-sm font-medium text-brand hover:text-brand-dark dark:text-brand-light dark:hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Notification Channel
      </button>

      {/* Error Message */}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Help Text */}
      {channels.length === 0 && (
        <p className="text-sm text-[var(--color-text-secondary)]">
          No notification channels configured. Add channels to receive alerts.
        </p>
      )}
    </div>
  );
};

export default NotificationChannelEditor;
